import { useEffect, useMemo, useState, type FC } from 'react';
import { useParams } from 'react-router';
import { Link as RouterLink } from 'react-router';
import { observer } from 'mobx-react-lite';
import { Badge, Popconfirm, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Link, Space, Text, Title } from '@g2u/ui-kit';

import { RootStore } from 'store';
import { AwayStatusModal, Content } from 'components';
import { Condition, TechnicianRecurringAvailabilityEntry } from 'types';
import { useTechnician } from 'helpers/hooks/useTechnician';
import {
  formatAwayDate,
  formatDayCount,
  inclusiveDayCount,
  NO_END_DATE,
  parseApiDate,
} from 'pages/technician/edit/helpers/availability';
import { useTechnicianAwayStatuses } from './hooks/useTechnicianAwayStatuses';
import styles from './away-status.module.scss';

export const TechnicianAwayStatusPage: FC = observer(() => {
  const { id } = useParams();
  const { setBreadcrumbs } = RootStore;
  const { technician } = useTechnician(id);
  const isBlacklisted = technician?.blacklist === Condition.Yes;
  const availabilityUrl = `/technician/edit/${id}/availability`;

  const {
    awayStatuses,
    isAwayStatusesLoading,
    paginationMetadata,
    updateAwayStatus,
    isAwayStatusUpdating,
    deleteAwayStatus,
  } = useTechnicianAwayStatuses(id);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedAwayStatus, setEditedAwayStatus] = useState<TechnicianRecurringAvailabilityEntry | null>(null);

  const initialValues = useMemo(
    () =>
      editedAwayStatus
        ? {
            startDate: parseApiDate(editedAwayStatus.start_date),
            endDate: editedAwayStatus.end_date === NO_END_DATE ? undefined : parseApiDate(editedAwayStatus.end_date),
          }
        : undefined,
    [editedAwayStatus],
  );

  useEffect(() => {
    setBreadcrumbs([
      {
        url: '/technician/search',
        title: 'Find a technician',
      },
      {
        url: availabilityUrl,
        title: `Technician File ${id}`,
      },
      {
        title: 'Away Status',
      },
    ]);

    return () => {
      setBreadcrumbs(null);
    };
  }, [id]);

  const handleEdit = (awayStatus: TechnicianRecurringAvailabilityEntry) => {
    setEditedAwayStatus(awayStatus);
    setIsModalOpen(true);
  };

  const handleSubmit = (values: AwayStatusModal.Payload) => {
    updateAwayStatus(
      { technicianId: Number(id), awayStatusId: editedAwayStatus!.ID, ...values },
      { onSuccess: () => setIsModalOpen(false) },
    );
  };

  const columns: ColumnsType<TechnicianRecurringAvailabilityEntry> = [
    {
      key: 'startDate',
      title: 'Start Date',
      width: 205,
      render: (_, record) => formatAwayDate(record.start_date),
    },
    {
      key: 'endDate',
      title: 'End Date',
      width: 205,
      render: (_, record) => formatAwayDate(record.end_date),
    },
    {
      key: 'days',
      title: 'Days',
      width: 205,
      render: (_, record) =>
        record.end_date === NO_END_DATE
          ? '—'
          : formatDayCount(inclusiveDayCount(parseApiDate(record.start_date), parseApiDate(record.end_date))),
    },
    {
      key: 'addedBy',
      title: 'Added By',
      width: 205,
      render: (_, record) =>
        record.added_by ? (
          <>
            <div>{record.added_by.role}</div>
            <div>({record.added_by.name})</div>
          </>
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 100,
      render: (_, record) => (
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
          <Link.$ disabled={record.is_past} onClick={() => !record.is_past && handleEdit(record)}>
            Edit
          </Link.$>
          <Popconfirm
            icon={false}
            disabled={record.is_past}
            title={<Text.$>Delete the Away Status</Text.$>}
            description="Are you sure you want to delete this Away Status?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => deleteAwayStatus({ technicianId: Number(id), awayStatusId: record.ID })}
          >
            <Link.$ type="danger" disabled={record.is_past}>
              Delete
            </Link.$>
          </Popconfirm>
        </Space.$>
      ),
    },
  ];

  return (
    <Content dataTestId="technician-away-status-page">
      <title>Away Status</title>
      <Badge.Ribbon
        text={
          <>
            Technician: {technician?.contact1_title} {technician?.contact1_firstname} {technician?.contact1_surname}{' '}
            {isBlacklisted ? '| Blacklisted' : ''}
          </>
        }
        color={isBlacklisted ? 'red' : 'blue'}
        placement="start"
        style={{ top: 12 }}
      >
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large} className={styles.awayStatus__container}>
          <RouterLink to={availabilityUrl}>
            <ArrowLeftOutlined /> Availability
          </RouterLink>
          <Title.$ level={Title.Level.H5} noMargin>
            Away Status
          </Title.$>
          <Table
            rowKey="ID"
            className={styles.awayStatus__table}
            columns={columns}
            dataSource={awayStatuses}
            loading={isAwayStatusesLoading}
            pagination={{
              total: paginationMetadata.total,
              current: paginationMetadata.current_page,
              pageSize: paginationMetadata.pageSize,
              onChange: paginationMetadata.onChange,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50],
              showTotal: (total) => `Total ${total} items`,
            }}
          />
        </Space.$>
      </Badge.Ribbon>
      <AwayStatusModal
        isOpen={isModalOpen}
        initialValues={initialValues}
        isLoading={isAwayStatusUpdating}
        onSubmit={handleSubmit}
        onClose={() => setIsModalOpen(false)}
      />
    </Content>
  );
});
